import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Upload, Receipt } from "lucide-react";
import { InvoiceUploadDialog } from "@/components/InvoiceUploadDialog";
import { COMM_STATUS } from "@/pages/AdminGpCommissions";
import { CommissionGuide } from "@/components/gp/CommissionGuide";
import { useAuth } from "@/context/AuthContext";
import { PendingEarnings } from "@/components/DealerBalanceBar";
import { CommissionChart } from "@/components/gp/CommissionChart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const NUM = "text-right tabular-nums whitespace-nowrap";

export default function MyGpCommissions() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [up, setUp] = useState(null);
  const load = () => api.get("/greenpower/my-commissions").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const OFF = ["stornata", "da_recuperare", "in_arrivo", "annullata"];
  const tot = rows.filter((r) => !OFF.includes(r.status)).reduce((s, r) => s + (r.net || 0), 0);
  const incoming = rows.filter((r) => r.status === "in_arrivo").reduce((sum, row) => sum + (row.net || 0), 0);
  const visibleRows = user?.role === "admin" ? rows : rows.filter((r) => (r.net || 0) > 0 && !(user?.role === "dealer" && r.status === "in_arrivo"));
  return (
    <div className="space-y-6" data-testid="gp-my-commissions-page">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Gestione</p>
        <h1 className="font-head text-3xl font-bold tracking-tight">Provvigioni e guadagni</h1>
        <p className="text-sm text-muted-foreground mt-1">Contratti e servizi {tot > 0 && <> · maturato netto: <strong data-testid="gp-my-total">{euro(tot)}</strong></>}{user?.role === "agent" && incoming > 0 && <> · in arrivo: <strong data-testid="gp-my-incoming">{euro(incoming)}</strong></>}</p>
      </div>
      {user?.role === "dealer" && <PendingEarnings />}
      <CommissionGuide />
      <CommissionChart rows={rows} />
      <Card className="glow-border overflow-x-auto" data-testid="gp-my-table">
        <Table className="text-sm min-w-[1000px]">
          <TableHeader><TableRow>
            <TableHead>Cliente</TableHead><TableHead>Pratica</TableHead><TableHead>Servizio</TableHead><TableHead>Ruolo</TableHead>
            <TableHead className={NUM}>Lordo</TableHead><TableHead className={NUM}>Ritenuta</TableHead><TableHead className={NUM}>IVA</TableHead><TableHead className={NUM}>Netto</TableHead>
            <TableHead>Stato</TableHead><TableHead>Fattura</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
        {visibleRows.map((r) => (
          <TableRow key={r.id} data-testid={`gp-my-row-${r.id}`}>
            <TableCell className="font-medium">{r.cliente_nome || "—"}</TableCell>
            <TableCell><EntityLink kind={r.service || "energia"} id={r.pratica_id} className="font-mono text-xs" data-testid={`my-commission-practice-link-${r.id}`}>{r.id_esterno || "Apri pratica"}</EntityLink></TableCell>
            <TableCell><Badge variant="outline" className="text-[10px]">{({ internet: "Internet", spedizioni: "Spedizioni", energia: "Venergia" })[r.service || "energia"] || r.service}</Badge></TableCell>
            <TableCell className="text-muted-foreground">{r.role_label || "—"}</TableCell>
            <TableCell className={NUM}>{euro(r.gross)}</TableCell>
            <TableCell className={NUM}>{r.ritenuta ? euro(r.ritenuta) : "—"}</TableCell>
            <TableCell className={NUM}>{r.iva ? euro(r.iva) : "—"}</TableCell>
            <TableCell className={`${NUM} font-semibold`} data-testid={`gp-my-net-${r.id}`}>{euro(r.net)}</TableCell>
            <TableCell><Badge variant="outline">{COMM_STATUS[r.status] || r.status}</Badge>
              {r.status === "in_arrivo" && <span className="block text-[11px] text-primary mt-1" data-testid={`gp-my-expected-${r.id}`}>{r.expected_at ? `Previsto ${new Date(r.expected_at).toLocaleDateString("it-IT")}` : "In attesa di Venergia"}</span>}
              {r.status === "maturata" && <span className="block text-[11px] text-muted-foreground mt-1" data-testid={`gp-my-await-${r.id}`}>Aspettiamo il pagamento dell'operatore</span>}
              {r.status === "incassata" && r.authorizable_on && <span className="block text-[11px] text-primary mt-1" data-testid={`gp-my-authon-${r.id}`}>Autorizzabile dal {new Date(r.authorizable_on).toLocaleDateString("it-IT")}</span>}
              {r.status === "rifiutata" && <span className="block text-[11px] text-red-400 mt-1" data-testid={`gp-my-reject-${r.id}`}>{r.reject_reason}</span>}</TableCell>
            <TableCell className="text-xs whitespace-nowrap" data-testid={`gp-my-inv-${r.id}`}>{r.invoice_number ? `${r.invoice_number} · ${r.invoice_date} · ${euro(r.invoice_total)}` : "—"}</TableCell>
            <TableCell><div className="flex items-center justify-end gap-1">
              {r.mode === "invoice" && (
                <a href={`${BACKEND_URL}/api/greenpower/commissions/${r.id}/prefattura`} target="_blank" rel="noopener noreferrer" data-testid={`gp-my-pdf-${r.id}`}>
                  <Button size="sm" variant="outline" className="gap-1 h-7 text-xs"><FileText className="w-3.5 h-3.5" /> Pre-fattura</Button>
                </a>
              )}
              {r.mode === "invoice" && ["da_fatturare", "rifiutata"].includes(r.status) && (
                <Button size="sm" className="gap-1 h-7 text-xs" onClick={() => setUp(r)} data-testid={`gp-my-upload-${r.id}`}><Upload className="w-3.5 h-3.5" /> Carica fattura</Button>
              )}
              {r.invoice_file_id && (
                <a href={`${BACKEND_URL}/api/greenpower/commissions/${r.id}/fattura`} target="_blank" rel="noopener noreferrer" data-testid={`gp-my-invfile-${r.id}`}>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><Receipt className="w-4 h-4" /></Button>
                </a>
              )}
            </div></TableCell>
          </TableRow>
        ))}
        {visibleRows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-6" data-testid="gp-my-empty">Nessuna provvigione ancora maturata: arriva quando il contratto del cliente è attivo.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      {up && <InvoiceUploadDialog row={up} onClose={() => setUp(null)} onDone={() => { setUp(null); load(); }} />}
    </div>
  );
}
